import React, { useState } from "react";
import { GroupLabel } from "@logora/parliament.parliament_group.group_label";
import { Label } from "@logora/parliament.ui.label";
import { MemberBox } from "@logora/parliament.parliament_member.member_box";
import { Button } from "@mui/material";
import {
	StarOutline as StarOutlineIcon,
	ChevronRight,
} from "@mui/icons-material";
import cx from "classnames";
import styles from "./AmendmentBox.module.scss";

interface AmendmentBoxProps {
	resource: AmendmentProps;
	showFollowButton: boolean;
}

interface AmendmentProps {
	numeroLong: string;
	divisionArticleDesignationCourte: string;
	nombreCoSignataires: number;
	dateDepot: string;
	groupePolitiqueRef?: GroupePolitiqueProps;
	groupeParlementaire?: GroupePolitiqueProps;
	acteurRef?: AuteurProps;
	exposeSommaire: string;
	divisionArticleDesignation: string;
	sortAmendement?: string | undefined;
	alineaDesignation?: string;
	dispositif?: string;
	typeAuteur?: string;
	cartoucheInformatif?: string;
}

interface GroupePolitiqueProps {
	couleurAssociee?: string;
	libelle?: string;
	libelleAbrege?: string;
	libelleAbrev?: string;
}

interface AuteurProps {
	prenom: string;
	nom: string;
	groupePolitiqueRef?: GroupePolitiqueProps;
	groupeParlementaire?: GroupePolitiqueProps;
}

const colorVariants = {
	success: ["Adopté"],
	cancel: ["Rejeté", "Irrecevable", "Irrecevable 40"],
	pending: [],
	info: ["En traitement", "A discuter"],
	neutral: ["Retiré", "Non soutenu", "Tombé"],
	default: [],
};

export const AmendmentBox = ({
	resource,
	showFollowButton = false,
}: AmendmentBoxProps) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const toggleBody = () => {
		setIsExpanded(!isExpanded);
	};

	return (
		<div
			className={cx(styles.amendmentBoxContainer, {
				[styles.expanded]: isExpanded,
			})}
		>
			<div className={styles.amendmentBoxHeader}>
				<span className={styles.numero}>n°{resource.numeroLong}</span>
				<span className={styles.article}>
					{resource.divisionArticleDesignationCourte}
				</span>
				{resource.acteurRef?.groupeParlementaire && (
					<GroupLabel
						color={resource.acteurRef?.groupeParlementaire?.couleurAssociee}
						groupName={
							resource.acteurRef?.groupeParlementaire?.libelleAbrev ?? ""
						}
						size="small"
						className={styles.amendementGroupLabel}
					/>
				)}
				<MemberBox
					firstName={resource.acteurRef?.prenom || resource.typeAuteur || ""}
					lastName={resource.acteurRef?.nom || ""}
					layout="oneLine"
					isBold={false}
					size="small"
					imageUrl={undefined}
					onClick={undefined}
				/>
				{resource.nombreCoSignataires && resource.nombreCoSignataires > 0 ? (
					<span className={styles.cosignataires}>
						+{resource.nombreCoSignataires}
					</span>
				) : null}
				<div className={styles.headerButtons}>
					{showFollowButton && (
						<Button
							className={styles.button}
							startIcon={<StarOutlineIcon />}
							size="small"
						>
							<span>Suivre</span>
						</Button>
					)}
					<Label
						text={resource.sortAmendement ?? ""}
						className={styles.status}
						variantsObject={colorVariants}
						colorType={resource.sortAmendement}
						leftIcon={undefined}
						rightIcon={undefined}
					/>
				</div>
			</div>
			<div
				className={cx(styles.amendmentBoxBody, {
					[styles.expanded]: isExpanded,
				})}
				onClick={toggleBody}
				onKeyUp={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						toggleBody();
					}
				}}
				style={{ cursor: "pointer" }}
				data-testid="amendment-accordion"
			>
				<div
					className={cx(styles.amendmentBoxBodyContent, {
						[styles.expanded]: isExpanded,
					})}
				>
					{isExpanded ? (
						<div>
							<div className={styles.bodyHeader}>
								<div className={styles.bodyContent}>
									<div className={styles.boxHeader}>Dépôt</div>
									<div className={styles.boxBody}>
										{new Date(resource.dateDepot).toLocaleDateString()}
									</div>
								</div>
								<div className={styles.bodyContent}>
									<div className={styles.boxHeader}>Emplacement</div>
									{resource?.divisionArticleDesignation ?? ""}
									{resource.alineaDesignation &&
										` • ${resource.alineaDesignation}`}
								</div>
							</div>
							<div className={styles.bodyContentContainer}>
								<div className={styles.bodyContent}>
									<div className={styles.boxHeader}>Dispositif</div>
									<div className={styles.boxBody}>
										{resource.dispositif || resource.cartoucheInformatif}
									</div>
								</div>
								{resource.exposeSommaire && (
									<div className={styles.bodyContent}>
										<div className={styles.boxHeader}>Exposé des motifs</div>
										<div className={styles.boxBody}>
											{resource.exposeSommaire}
										</div>
									</div>
								)}
							</div>
						</div>
					) : (
						<div>{resource.exposeSommaire}</div>
					)}
				</div>
				<ChevronRight
					className={cx(styles.chevron, { [styles.expanded]: isExpanded })}
				/>
			</div>
		</div>
	);
};
